import { useEffect, useState } from "react";
import { LogOut, CalendarPlus, ListChecks, FileDown, Loader2, FolderOpen, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, downloadPdf, openPdf, errMsg, fmtDate, slotLabel } from "@/lib/api";
import { BRAND, LOGO, SERVICES, STATUS } from "@/lib/brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BookingForm } from "@/components/BookingForm";
import { Card, Empty, Pill } from "@/components/common";

const MyBookings = ({ items }) => (
  items.length === 0 ? <Empty>Nessun ritiro prenotato.</Empty> : (
    <div className="grid gap-3" data-testid="vet-bookings-list">
      {items.map((b) => (
        <Card key={b.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`vet-booking-${b.id}`}>
          <div>
            <p className="font-display text-xl font-semibold text-[#0D3B52]">{fmtDate(b.date)} · {slotLabel(b.slot_start)}</p>
            <p className="text-sm text-[#5C6B73]">{b.pet_name || "—"} ({b.species}) · {SERVICES[b.service_type]} · {b.pickup_address} {b.city}</p>
          </div>
          <Pill cls={STATUS[b.status]?.cls}>{STATUS[b.status]?.label}</Pill>
        </Card>
      ))}
    </div>
  )
);

const MyDdts = ({ items }) => {
  const [busy, setBusy] = useState(null);
  const dl = async (d) => {
    setBusy(d.id);
    try { await downloadPdf(`/ddts/${d.id}/pdf`, `DDT_${d.numero.replace("/", "-")}.pdf`); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  if (items.length === 0) return <Empty>Nessun documento di trasporto disponibile.</Empty>;
  return (
    <div className="grid gap-3" data-testid="vet-ddts-list">
      {items.map((d) => (
        <Card key={d.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="font-display text-xl font-semibold text-[#0D3B52]">DDT n. {d.numero} del {fmtDate(d.data)}</p>
            <p className="text-sm text-[#5C6B73]">{d.righe?.map((r) => r.specie).filter(Boolean).join(", ") || "—"} · {d.cliente_nome || d.origine_nome}</p>
          </div>
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => openPdf(`/ddts/${d.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`vet-ddt-view-${d.id}`} className="rounded-full"><Eye className="h-4 w-4 mr-2" />Visualizza</Button>
          <Button onClick={() => dl(d)} disabled={busy === d.id} data-testid={`vet-ddt-download-${d.id}`} className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">
            {busy === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Scarica PDF
          </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

const MyFiles = ({ items }) => {
  const dl = (f) => downloadPdf(`/files/${f.id}/download`, f.original_filename).catch((e) => toast.error(errMsg(e)));
  if (items.length === 0) return <Empty>Nessun file caricato dall'agenzia.</Empty>;
  return (
    <div className="grid gap-3" data-testid="vet-files-list">
      {items.map((f) => (
        <Card key={f.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="font-display text-xl font-semibold text-[#0D3B52]">{f.title}</p>
            <p className="text-sm text-[#5C6B73]">Caricato il {fmtDate(f.created_at?.slice(0, 10))} · {(f.size / 1024).toFixed(0)} KB</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openPdf(`/files/${f.id}/download`).catch((e) => toast.error(errMsg(e)))} data-testid={`vet-file-view-${f.id}`} className="rounded-full"><Eye className="h-4 w-4 mr-2" />Visualizza</Button>
            <Button onClick={() => dl(f)} data-testid={`vet-file-dl-${f.id}`} className="rounded-full bg-[#0D3B52] hover:bg-[#082535]"><FileDown className="h-4 w-4 mr-2" />Scarica PDF</Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default function VetDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [ddts, setDdts] = useState([]);
  const [files, setFiles] = useState([]);
  const load = () => {
    api.get("/vet/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/vet/ddts").then((r) => setDdts(r.data)).catch(() => {});
    api.get("/vet/files").then((r) => setFiles(r.data)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="vet-dashboard">
      <header className="bg-white border-b border-[#E2DDD5]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <img src={LOGO} alt={BRAND.name} className="h-9 sm:h-11 w-auto" />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#5C6B73]" data-testid="vet-clinic-name">{user.clinic_name}</span>
            <Button variant="outline" onClick={logout} data-testid="vet-logout-button" className="rounded-full"><LogOut className="h-4 w-4 mr-2" />Esci</Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-3xl bg-[#0D3B52] text-white p-8 sm:p-10 relative overflow-hidden grain rise">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">Area veterinari</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Benvenuto, {user.name || user.clinic_name}</h1>
          <p className="text-sm text-white/70 mt-2">Prenota un ritiro, segui lo stato delle richieste e scarica i documenti di trasporto.</p>
        </div>
        <Tabs defaultValue="prenota" className="mt-8">
          <TabsList className="bg-white border border-[#E2DDD5] h-auto p-1 flex-wrap">
            <TabsTrigger value="prenota" data-testid="vet-tab-prenota" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white px-4 py-2"><CalendarPlus className="h-4 w-4 mr-2" />Prenota ritiro</TabsTrigger>
            <TabsTrigger value="ritiri" data-testid="vet-tab-ritiri" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white px-4 py-2"><ListChecks className="h-4 w-4 mr-2" />I miei ritiri ({bookings.length})</TabsTrigger>
            <TabsTrigger value="ddt" data-testid="vet-tab-ddt" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white px-4 py-2"><FileDown className="h-4 w-4 mr-2" />Documenti di trasporto ({ddts.length})</TabsTrigger>
            <TabsTrigger value="file" data-testid="vet-tab-file" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white px-4 py-2"><FolderOpen className="h-4 w-4 mr-2" />File dall'agenzia ({files.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="prenota" className="mt-6">
            <Card className="p-6 sm:p-8">
              <BookingForm mode="vet" defaults={{ pickup_address: user.address || "", city: user.city || "" }} onDone={load} />
            </Card>
          </TabsContent>
          <TabsContent value="ritiri" className="mt-6"><MyBookings items={bookings} /></TabsContent>
          <TabsContent value="ddt" className="mt-6"><MyDdts items={ddts} /></TabsContent>
          <TabsContent value="file" className="mt-6"><MyFiles items={files} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
